/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useSpeedDialForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing Speed Dial form state and validation.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import type { SpeedDialFormState } from './useSpeedDialData';

// Speed code regex: allows *, #, 0-9, A-Z, a-z, _, - (1-32 chars)
const SPEED_CODE_REGEX = /^[*#0-9A-Za-z_-]{1,32}$/;

export interface FormErrors {
  [key: string]: string;
}

export function useSpeedDialForm(
  formData: SpeedDialFormState,
  baseline: SpeedDialFormState | null,
  mode: 'create' | 'edit',
  setFormData: (data: SpeedDialFormState | ((prev: SpeedDialFormState) => SpeedDialFormState)) => void
) {
  const { t } = useI18n();
  const [errors, setErrors] = useState<FormErrors>({});

  const hasChanges = useMemo(() => {
    if (!baseline) return true; // If no baseline, assume there are changes (e.g., create mode)

    const fieldsToCompare: Array<keyof SpeedDialFormState> = [
      'speedCode',
      'destination',
      'description',
      'classOfServicesId',
      'enabled'
    ];

    for (const field of fieldsToCompare) {
      const currentValue = formData[field];
      const originalValue = baseline[field];

      // Handle null vs undefined as the same
      if ((currentValue == null) !== (originalValue == null)) {
        return true;
      }

      if (currentValue != null && originalValue != null) {
        if (String(currentValue) !== String(originalValue)) {
          return true;
        }
      }
    }
    return false;
  }, [formData, baseline]);

  const handleInputChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (field: keyof SpeedDialFormState, value: any) => {
      setFormData(prev => ({ ...prev, [field]: value }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    },
    [errors, setFormData]
  );

  const validateForm = useCallback(() => {
    const newErrors: FormErrors = {};

    if (!formData.speedCode || !formData.speedCode.trim()) {
      newErrors.speedCode = t('speedDials.fields.speedCode.required');
    } else if (!SPEED_CODE_REGEX.test(formData.speedCode)) {
      newErrors.speedCode = t('speedDials.fields.speedCode.invalid', 'Speed code contains invalid characters');
    }

    if (!formData.destination || !formData.destination.trim()) {
      newErrors.destination = t('speedDials.fields.destination.required');
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, [formData, t]);

  return {
    errors,
    setErrors,
    hasChanges,
    handleInputChange,
    validateForm
  };
}

